import React, { useEffect, useState } from 'react';
import Select, { ValueType } from 'react-select';
import EdiText from 'react-editext';
import Dropzone from 'react-dropzone-uploader';
import AddBoxIcon from '@material-ui/icons/AddBox';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { Tabs, TabsPosition } from '../../../../../shared/components/group-ui/tabs/tabs.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import { Loader } from '../../../../../shared/components/core-ui/loader/loader.component';
import { GuestUiModel } from '../../../../../shared/ui-models/guest.ui-model';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { GuestDocumentType } from '../../../../../shared/models/guest.model';
import { FILE_TYPES } from '../../../../../shared/config/files-types.config';
import { PUBLIC_PATH } from '../../../../../shared/config/routes-config';
import { hasAccess } from '../../../../../shared/services/helpers/auth-helper/auth-helper.service';
import { PERMISSIONS_LIST } from '../../../../../shared/config/permissions.config';
import { GUEST_VISIT_TYPES } from '../../reservation-edit.helper';
import COUNTRIES from '../add-guest-modal/countries.json';
import { ErrorInfo, isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import {
  getOneGuest,
  updateGuest,
  deleteGuest,
  uploadGuestDocument,
  updateOneReservations
} from '../../../../shared/services/data.service';
import { birdToast } from '../../../../../shared/components/core-ui/toast';

export interface GuestSectionProps {
  data: {
    reservation: ReservationDetailsUiModel;
  };
  eventHandlers: {
    onAddGuest: () => void;
    onGetOneReservation: (id: string) => void;
  };
}

export const GuestSection: React.FC<GuestSectionProps> = ({
  data: { reservation },
  eventHandlers: { onAddGuest, onGetOneReservation }
}) => {
  const [guest, setGuest] = useState<GuestUiModel | undefined>(undefined);
  const [showUpdateLoader, setShowUpdateLoader] = useState(false);
  const [isNationalIdUploadLoading, setIsNationalIdUploadLoading] = useState(false);
  const [isPassportUploadLoading, setIsPassportUploadLoading] = useState(false);
  const [isMarriageUploadLoading, setIsMarriageUploadLoading] = useState(false);
  const [isLoadingDeleteGuest, setIsLoadingDeleteGuest] = useState(false);

  useEffect(() => {
    if (reservation?.guestIds && reservation.guestIds.length) {
      getOneGuestObject(reservation.guestIds[0]);
    } else {
      setGuest(undefined);
    }
  }, [reservation?.guestIds]);

  function getOneGuestObject(id: string) {
    getOneGuest(id).then((response: GuestUiModel | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        setGuest(response);
      }
    });
  }

  function updateOneGuest(guestData: GuestUiModel) {
    setShowUpdateLoader(true);
    return updateGuest(guestData).then((response: GuestUiModel | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        setGuest(response);
        setShowUpdateLoader(false);
      }
    });
  }

  function deleteOneGuest(guestId: string) {
    setIsLoadingDeleteGuest(true);
    return deleteGuest(guestId)
      .then((response: GuestUiModel | ErrorInfo) => {
        if (!isErrorInfo(response)) {
          setGuest(response);
          return updateOneReservations({
            ...reservation,
            guestIds: reservation?.guestIds.filter((id) => guest?.id != id)
          }).then(() => onGetOneReservation(reservation?.id || ''));
        }
      })
      .finally(() => setIsLoadingDeleteGuest(false));
  }

  function handleUploadDocument(
    file: File,
    guestId: string,
    reservationId: string,
    type: GuestDocumentType
  ) {
    uploadGuestDocument(file, guestId, reservationId, type).then(() => {
      onGetOneReservation(reservationId);
      if (type === GuestDocumentType.NATIONAL_ID) setIsNationalIdUploadLoading(false);
      if (type === GuestDocumentType.PASSPORT) setIsPassportUploadLoading(false);
      if (type === GuestDocumentType.MARRIAGE_DOC) setIsMarriageUploadLoading(false);
    });
  }

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 w-full flex flex-col">
      {!reservation.guestIds || reservation.guestIds.length === 0 ? (
        <div className="flex justify-center items-center flex-col">
          <img src={`${PUBLIC_PATH}/assets/pngs/guest.png`} alt="add-user" className="size-96" />
          <p className="mb-4 font-gilroy-medium text-neutral-500">
            No Guests added yet, click below to add first guest
          </p>
          <Button onClick={onAddGuest} size={ButtonSize.medium}>
            Add Guest
          </Button>
        </div>
      ) : (
        <></>
      )}
      {reservation.guestIds && reservation.guestIds.length ? (
        <div className="flex flex-row justify-between">
          <div className="h-[10%]">
            <Tabs
              data={{
                tabsList: reservation
                  ? reservation.guestIds &&
                    reservation.guestIds.map((guestId, index) => ({
                      component: <></>,
                      key: `Guest ${index + 1}`,
                      id: guestId
                    }))
                  : [],
                defaultTab: 'Guest 1'
              }}
              style={{ position: TabsPosition.LEFT }}
              eventHandlers={{
                tabChangeHandler: (tabKey, id) => {
                  if (id) {
                    getOneGuestObject(id);
                  }
                }
              }}
            />
          </div>
          {hasAccess([
            PERMISSIONS_LIST.reservationsFullAccess,
            PERMISSIONS_LIST.reservationsModifyAccess
          ]) && (
            <div className="cursor-pointer text-[3.5rem]" onClick={onAddGuest}>
              <AddBoxIcon color="primary" fontSize="inherit" />
            </div>
          )}
        </div>
      ) : (
        <></>
      )}
      {guest && !showUpdateLoader ? (
        <div className="flex flex-col md:flex-row md:flex-wrap">
          <div className="w-full mt-[1.5rem] md:w-[50%] md:pr-[1rem]">
            <div className="mb-[1rem] font-['Abhaya_Libre_Medium'] font-medium text-[1.1rem] uppercase text-[rgba(0,0,0,0.7)]">
              First Name
            </div>
            <div className="font-['Abel'] text-[1.6rem] whitespace-nowrap overflow-hidden text-ellipsis">
              {hasAccess([
                PERMISSIONS_LIST.reservationsFullAccess,
                PERMISSIONS_LIST.reservationsModifyAccess
              ]) ? (
                <EdiText
                  type="text"
                  value={guest.firstName}
                  onSave={(val) => {
                    updateOneGuest({
                      ...guest,
                      firstName: val
                    });
                  }}
                />
              ) : (
                <span>{guest.firstName}</span>
              )}
            </div>
          </div>
          <div className="w-full mt-[1.5rem] md:w-[50%] md:pr-[1rem]">
            <div className="mb-[1rem] font-['Abhaya_Libre_Medium'] font-medium text-[1.1rem] uppercase text-[rgba(0,0,0,0.7)]">
              Last Name
            </div>
            <div className="font-['Abel'] text-[1.6rem] whitespace-nowrap overflow-hidden text-ellipsis">
              {hasAccess([
                PERMISSIONS_LIST.reservationsFullAccess,
                PERMISSIONS_LIST.reservationsModifyAccess
              ]) ? (
                <EdiText
                  type="text"
                  value={guest.lastName}
                  onSave={(val) => {
                    updateOneGuest({ ...guest, lastName: val });
                  }}
                />
              ) : (
                <span>{guest.lastName}</span>
              )}
            </div>
          </div>
          <div className="w-full mt-[1.5rem] md:w-[50%] md:pr-[1rem]">
            <div className="mb-[1rem] font-['Abhaya_Libre_Medium'] font-medium text-[1.1rem] uppercase text-[rgba(0,0,0,0.7)]">
              Phone number
            </div>
            <div className="font-['Abel'] text-[1.6rem] whitespace-nowrap overflow-hidden text-ellipsis">
              {hasAccess([
                PERMISSIONS_LIST.reservationsFullAccess,
                PERMISSIONS_LIST.reservationsModifyAccess
              ]) ? (
                <EdiText
                  type="text"
                  value={String(guest.phoneNumber)}
                  onSave={(val) => {
                    updateOneGuest({ ...guest, phoneNumber: val });
                  }}
                />
              ) : (
                <span>{guest.phoneNumber}</span>
              )}
            </div>
          </div>
          <div className="w-full mt-[1.5rem] md:w-[50%] md:pr-[1rem]">
            <div className="mb-[1rem] font-['Abhaya_Libre_Medium'] font-medium text-[1.1rem] uppercase text-[rgba(0,0,0,0.7)]">
              Email
            </div>
            <div className="font-['Abel'] text-[1.6rem] whitespace-nowrap overflow-hidden text-ellipsis">
              {hasAccess([
                PERMISSIONS_LIST.reservationsFullAccess,
                PERMISSIONS_LIST.reservationsModifyAccess
              ]) ? (
                <EdiText
                  type="text"
                  value={guest.email}
                  onSave={(val) => {
                    updateOneGuest({ ...guest, email: val });
                  }}
                />
              ) : (
                <span>{guest.email}</span>
              )}
            </div>
          </div>
          {/* nationality */}
          <div className="w-full mt-[1.5rem] md:w-[50%] md:pr-[1rem]">
            <div className="mb-[1rem] font-['Abhaya_Libre_Medium'] font-medium text-[1.1rem] uppercase text-[rgba(0,0,0,0.7)]">
              Nationality
            </div>
            <div className="font-['Abel'] text-[1.6rem] whitespace-nowrap overflow-hidden text-ellipsis">
              {hasAccess([
                PERMISSIONS_LIST.reservationsFullAccess,
                PERMISSIONS_LIST.reservationsModifyAccess
              ]) ? (
                <Select
                  isSearchable
                  options={COUNTRIES}
                  defaultValue={COUNTRIES.find((val) => val.value === guest.nationality)}
                  onChange={async (value: ValueType<{ value: string; label: string }, false>) => {
                    await updateOneGuest({ ...guest, nationality: value?.value });
                    window.location.reload();
                  }}
                  styles={{
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                      position: 'relative'
                    })
                  }}
                />
              ) : (
                <span>{guest.nationality}</span>
              )}
            </div>
          </div>

          <div className="w-full mt-[1.5rem] md:w-[50%] md:pr-[1rem]">
            <div className="mb-[1rem] font-['Abhaya_Libre_Medium'] font-medium text-[1.1rem] uppercase text-[rgba(0,0,0,0.7)]">
              Purpose of stay
            </div>
            <div className="font-['Abel'] text-[1.6rem] whitespace-nowrap overflow-hidden text-ellipsis">
              {hasAccess([
                PERMISSIONS_LIST.reservationsFullAccess,
                PERMISSIONS_LIST.reservationsModifyAccess
              ]) ? (
                <Select
                  isSearchable
                  options={GUEST_VISIT_TYPES}
                  defaultValue={GUEST_VISIT_TYPES.find((val) => val.value === guest.purposeOfStay)}
                  onChange={async (value: ValueType<{ value: string; label: string }, false>) => {
                    await updateOneGuest({ ...guest, purposeOfStay: value?.value });
                    window.location.reload();
                  }}
                  styles={{
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                      position: 'relative'
                    })
                  }}
                />
              ) : (
                <span>{guest.purposeOfStay}</span>
              )}
            </div>
          </div>
          {hasAccess([
            PERMISSIONS_LIST.reservationsFullAccess,
            PERMISSIONS_LIST.reservationsModifyAccess,
            PERMISSIONS_LIST.reservationsReadAccess
          ]) && (
            <div
              className="
            w-full mt-[1.5rem]  md:pr-[1rem] md:w-full"
            >
              <div className="mb-[1rem] font-['Abhaya_Libre_Medium'] font-medium text-[1.1rem] uppercase text-[rgba(0,0,0,0.7)]">
                National ID:
              </div>
              {guest.document ? (
                guest.document
                  .filter((doc) => !doc.type || doc.type === GuestDocumentType.NATIONAL_ID)
                  .map((doc, index) => (
                    <a href={doc.documentUrl}>
                      <div className="border border-grey rounded-[4px] mb-[1rem] flex justify-center p-[1rem] no-underline">
                        {doc.documentUrl &&
                        doc.documentUrl.split('.').length > -1 &&
                        FILE_TYPES[`${doc.documentUrl.split('.').pop()?.toUpperCase()}`] ? (
                          <img
                            className="w-[60px] mr-[1rem]"
                            src={FILE_TYPES[doc.documentUrl.split('.').pop().toUpperCase()].image}
                          ></img>
                        ) : (
                          <img
                            className="w-[60px] mr-[1rem]"
                            src={`${PUBLIC_PATH}/assets/pngs/file-icon.png`}
                          ></img>
                        )}
                        <div className="flex flex-col w-[calc(100%-60px)]">
                          <div className="flex-1 whitespace-nowrap text-ellipsis overflow-hidden text-black text-[1.6rem] font-['Abhaya_Libre_Medium'] font-medium uppercase text-[rgba(0,0,0,0.7)]">
                            {`National ID ${index + 1}`}
                          </div>
                          <div className="flex-1 whitespace-nowrap text-ellipsis overflow-hidden text-lightgrey text-[1.2rem]">
                            {doc.documentId}
                          </div>
                        </div>
                      </div>
                    </a>
                  ))
              ) : (
                <></>
              )}
              <Dropzone
                classNames={{
                  input: '!hidden'
                }}
                inputContent={() => (
                  <div className="font-['Abhaya_Libre_Medium'] font-medium text-[1.1rem] uppercase text-[rgba(0,0,0,0.7)] flex flex-col items-center justify-center text-center">
                    {isNationalIdUploadLoading ? (
                      <img src={`${PUBLIC_PATH}/assets/svgs/loader.svg`} alt="loader" />
                    ) : (
                      <>
                        <CloudUploadIcon fontSize={'large'} />
                        Upload Identification Document
                      </>
                    )}
                  </div>
                )}
                accept="*"
                maxFiles={1}
                multiple={false}
                onSubmit={(files) => {
                  setIsNationalIdUploadLoading(true);
                  files.forEach((file) => {
                    handleUploadDocument(
                      file.file,
                      guest.id || '',
                      reservation.id,
                      GuestDocumentType.NATIONAL_ID
                    );
                    file.remove();
                  });
                }}
              />
              <div className="mb-[1rem] font-['Abhaya_Libre_Medium'] font-medium text-[1.1rem] uppercase text-[rgba(0,0,0,0.7)]">
                Passport:
              </div>
              {guest.document ? (
                guest.document
                  .filter((doc) => doc.type === GuestDocumentType.PASSPORT)
                  .map((doc, index) => (
                    <a href={doc.documentUrl}>
                      <div className="border border-grey rounded-[4px] mb-[1rem] flex justify-center p-[1rem] no-underline">
                        {doc.documentUrl &&
                        doc.documentUrl.split('.').length > -1 &&
                        FILE_TYPES[`${doc.documentUrl.split('.').pop()?.toUpperCase()}`] ? (
                          <img
                            className="w-[60px] mr-[1rem]"
                            src={FILE_TYPES[doc.documentUrl.split('.').pop().toUpperCase()].image}
                          ></img>
                        ) : (
                          <img
                            className="w-[60px] mr-[1rem]"
                            src={`${PUBLIC_PATH}/assets/pngs/file-icon.png`}
                          ></img>
                        )}
                        <div className="flex flex-col w-[calc(100%-60px)]">
                          <div className="flex-1 whitespace-nowrap text-ellipsis overflow-hidden text-black text-[1.6rem] font-['Abhaya_Libre_Medium'] font-medium uppercase text-[rgba(0,0,0,0.7)]">
                            {`Passport Document ${index + 1}`}
                          </div>
                          <div className="flex-1 whitespace-nowrap text-ellipsis overflow-hidden text-lightgrey text-[1.2rem]">
                            {doc.documentId}
                          </div>
                        </div>
                      </div>
                    </a>
                  ))
              ) : (
                <></>
              )}
              <Dropzone
                classNames={{
                  input: '!hidden'
                }}
                inputContent={() => (
                  <div className="font-['Abhaya_Libre_Medium'] font-medium text-[1.1rem] uppercase text-[rgba(0,0,0,0.7)] flex flex-col items-center justify-center text-center">
                    {isPassportUploadLoading ? (
                      <img src={`${PUBLIC_PATH}/assets/svgs/loader.svg`} alt="loader" />
                    ) : (
                      <>
                        <CloudUploadIcon fontSize={'large'} />
                        Upload Passport Document
                      </>
                    )}
                  </div>
                )}
                accept="*"
                maxFiles={1}
                multiple={false}
                onSubmit={(files) => {
                  setIsPassportUploadLoading(true);
                  files.forEach((file) => {
                    handleUploadDocument(
                      file.file,
                      guest.id || '',
                      reservation.id,
                      GuestDocumentType.PASSPORT
                    );
                    file.remove();
                  });
                }}
              />
              <div className="mb-[1rem] font-['Abhaya_Libre_Medium'] font-medium text-[1.1rem] uppercase text-[rgba(0,0,0,0.7)]">
                Marriage Certificate:
              </div>
              {guest.document ? (
                guest.document
                  .filter((doc) => doc.type === GuestDocumentType.MARRIAGE_DOC)
                  .map((doc, index) => (
                    <a href={doc.documentUrl}>
                      <div className="border border-grey rounded-[4px] mb-[1rem] flex justify-center p-[1rem] no-underline">
                        {doc.documentUrl &&
                        doc.documentUrl.split('.').length > -1 &&
                        FILE_TYPES[`${doc.documentUrl.split('.').pop()?.toUpperCase()}`] ? (
                          <img
                            className="w-[60px] mr-[1rem]"
                            src={FILE_TYPES[doc.documentUrl.split('.').pop().toUpperCase()].image}
                          ></img>
                        ) : (
                          <img
                            className="w-[60px] mr-[1rem]"
                            src={`${PUBLIC_PATH}/assets/pngs/file-icon.png`}
                          ></img>
                        )}
                        <div className="flex flex-col w-[calc(100%-60px)]">
                          <div className="flex-1 whitespace-nowrap text-ellipsis overflow-hidden text-black text-[1.6rem] font-['Abhaya_Libre_Medium'] font-medium uppercase text-[rgba(0,0,0,0.7)]">
                            {`Marriage Document ${index + 1}`}
                          </div>
                          <div className="flex-1 whitespace-nowrap text-ellipsis overflow-hidden text-lightgrey text-[1.2rem]">
                            {doc.documentId}
                          </div>
                        </div>
                      </div>
                    </a>
                  ))
              ) : (
                <></>
              )}

              <Dropzone
                classNames={{
                  input: '!hidden'
                }}
                inputContent={() => (
                  <div className="font-['Abhaya_Libre_Medium'] font-medium text-[1.1rem] uppercase text-[rgba(0,0,0,0.7)] flex flex-col items-center justify-center text-center">
                    {isMarriageUploadLoading ? (
                      <img src={`${PUBLIC_PATH}/assets/svgs/loader.svg`} alt="loader" />
                    ) : (
                      <>
                        <CloudUploadIcon fontSize={'large'} />
                        Upload Marriage Certifcate
                      </>
                    )}
                  </div>
                )}
                accept="*"
                maxFiles={1}
                multiple={false}
                onSubmit={(files) => {
                  setIsMarriageUploadLoading(true);
                  files.forEach((file) => {
                    handleUploadDocument(
                      file.file,
                      guest.id || '',
                      reservation.id,
                      GuestDocumentType.MARRIAGE_DOC
                    );
                    file.remove();
                  });
                }}
              />
            </div>
          )}
          <Button
            type={ButtonType.danger}
            className="flex whitespace-nowrap !px-[2rem] !py-[1.25rem] items-center justify-center"
            onClick={() => {
              deleteOneGuest(guest.id || '').then(() =>
                birdToast.success(`successfully delete ${guest.firstName}.`)
              );
            }}
            loading={isLoadingDeleteGuest}
            size={ButtonSize.medium}
          >
            delete guest
          </Button>
        </div>
      ) : (
        showUpdateLoader && (
          <div className="absolute top-[50%] left-[57%]">
            <Loader />
          </div>
        )
      )}
    </div>
  );
};
